<template>
	<div>
		<h4 class="mb-3">{{ $t('map-columns') }}</h4>
		<p class="text-muted small mb-3">{{ $t('match-csv-columns-to-thirdparty-fields') }}</p>

		<div class="form-group">
			<label>{{ $t('which-column-identifies-the-student') }}</label>
			<select class="form-control" style="max-width: 400px;" v-model="localStudentIdColumnIndex">
				<option :value="null">{{ $t('none-manual-selection-only') }}</option>
				<option v-for="(header, index) in csvCols" :value="index" :key="index">
					{{ $t('col') }} {{ index }}: {{ header || $t('empty-parens') }}
				</option>
			</select>
		</div>

		<div class="table-responsive mb-3">
			<table class="table table-sm">
				<thead class="thead-light">
					<tr>
						<th>{{ $t('csv-column') }}</th>
						<th style="width:22%">{{ $t('target') }}</th>
						<th style="width:18%">{{ $t('new-column-name') }}</th>
						<th style="width:14%">{{ $t('datatype') }}</th>
						<th>{{ $t('sample-value') }}</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(header, index) in csvCols" :key="index">
						<td>
							{{ header || $t('empty-parens') }}
							<b-icon v-if="index === localStudentIdColumnIndex" icon="person-badge" class="text-primary ml-1" :title="$t('student-identifier-column')"></b-icon>
						</td>
						<td>
							<span v-if="index === localStudentIdColumnIndex" class="text-muted small">{{ $t('student-identifier-column') }}</span>
							<SelectSearch
								v-else
								:value="targetValue(index)"
								:options="targetOptions(index)"
								optionLabel="caption"
								:nullLabel="$t('select-caps')"
								:error="targetInvalid(index)"
								@input="onTargetInput(index, $event)"
							/>
						</td>
						<td>
							<b-form-input
								v-if="colAssign[index] === 'create'"
								v-model="createdCols[index]"
								size="sm"
								:placeholder="header"
							></b-form-input>
							<span v-else class="text-muted">—</span>
						</td>
						<td>
							<b-form-select v-if="showDatatype(index)" v-model="datatype[index]" size="sm" :state="datatypeState(index)">
								<option value="">{{ $t('datatype') }}</option>
								<option value="0">{{ $t('number') }}</option>
								<option value="1">{{ $t('date') }}</option>
								<option value="2">{{ $t('string') }}</option>
							</b-form-select>
							<span v-else class="text-muted">—</span>
						</td>
						<td class="text-muted small">{{ previewValueFor(index) }}</td>
					</tr>
				</tbody>
			</table>
		</div>

		<div class="d-flex">
			<b-button variant="outline-secondary" class="mr-2" @click="$emit('back')">← {{ $t('back') }}</b-button>
			<b-button variant="primary" :disabled="!mappingValid" @click="$emit('next')">
				{{ $t('next') }}: {{ $t('review-students') }} →
			</b-button>
		</div>
	</div>
</template>

<script>
import SelectSearch from 'src/components/Inputs/SelectSearch.vue';

export default {
	components: {
		SelectSearch,
	},
	props: {
		csvCols:               { type: Array,  required: true },
		existingCols:          { type: Object,  required: true },
		existingColsArr:       { type: Array,  required: true },
		colAssign:             { type: Object,  required: true },
		datatype:              { type: Object,  required: true },
		createdCols:           { type: Array,  required: true },
		studentIdColumnIndex:  { type: [Number, String], default: null },
		mappingValid:          { type: Boolean, default: false },
		previewRows:           { type: Array,  default: () => [] },
	},
	computed: {
		localStudentIdColumnIndex: {
			get()  { return this.studentIdColumnIndex; },
			set(v) { this.$emit('update:studentIdColumnIndex', v === null ? null : Number(v)); },
		},
	},
	methods: {
		isColUsedElsewhere(col, currentIndex) {
			return this.csvCols.some((_, i) => Number(i) !== Number(currentIndex) && this.colAssign[i] === col);
		},
		targetOptions(index) {
			const cols = Object.keys(this.existingCols)
				.map(key => this.existingCols[key])
				.filter(col => !this.isColUsedElsewhere(col, index));
			return [
				{ id: '__ignore__', special: 'ignore', caption: this.$t('ignore') },
				{ id: '__create__', special: 'create', caption: this.$t('create') },
				...cols,
			];
		},
		targetValue(index) {
			const current = this.colAssign[index];
			if (current === 'ignore') return { id: '__ignore__', special: 'ignore', caption: this.$t('ignore') };
			if (current === 'create') return { id: '__create__', special: 'create', caption: this.$t('create') };
			if (current && typeof current === 'object') return current;
			return null;
		},
		onTargetInput(index, option) {
			this.colAssign[index] = option && option.special ? option.special : option;
			this.$emit('select-change', index);
		},
		targetInvalid(index) {
			return this.colAssign[index] === '' || this.colAssign[index] === undefined;
		},
		showDatatype(index) {
			return index !== this.localStudentIdColumnIndex && this.colAssign[index] !== '' && this.colAssign[index] !== 'ignore' && this.colAssign[index] !== undefined;
		},
		datatypeState(index) {
			if (!this.showDatatype(index)) return null;
			return this.datatype[index] === '' ? false : null;
		},
		previewValueFor(index) {
			if (!this.previewRows.length) return '';
			return this.previewRows[0][index] ?? '';
		},
	},
};
</script>
